import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ROLE_LABELS, isStaff, houseCoverUrl, mapsUrl } from "@/lib/constants";
import { KeyBadge } from "@/components/KeyBadge";
import { NoticeBoard } from "@/components/NoticeBoard";
import { HouseDetailsCard } from "@/components/HouseDetailsCard";
import { ServiceRatesCard } from "@/components/ServiceRatesCard";
import { RecentCommentsCard } from "@/components/RecentCommentsCard";
import { WelcomeVisibilityMenu } from "@/components/WelcomeVisibilityMenu";
import { ArrowLeft, MapPin, BookOpen, ExternalLink, Trash2, Loader2, CalendarDays, Check, Globe, Plus, Settings, Users as UsersIcon, ListChecks, KeyRound, Camera, MessageCircle, Pencil, Shirt } from "lucide-react";
import { LINEN_ITEMS } from "@/lib/linenItems";
import { toast } from "sonner";

import { tx, lz, uiLocale } from "@/i18n";
const NONE = "__none__";
const LANGS = { nl: "Nederlands", en: "English", de: "Deutsch", fr: "Français", es: "Español" };
const FLAG = { nl: "🇳🇱", en: "🇬🇧", de: "🇩🇪", fr: "🇫🇷", es: "🇪🇸" };

export default function HouseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [house, setHouse] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [savingCal, setSavingCal] = useState(false);
  const [viewBook, setViewBook] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [books, setBooks] = useState([]);
  const [newBook, setNewBook] = useState({ language: "nl", url: "" });
  const [savingBooks, setSavingBooks] = useState(false);
  const [contentView, setContentView] = useState(false);
  const [editContent, setEditContent] = useState(false);
  const [sections, setSections] = useState([]);
  const [savingContent, setSavingContent] = useState(false);
  const [bookLang, setBookLang] = useState("nl");
  const [translating, setTranslating] = useState(false);
  const [latestLinen, setLatestLinen] = useState(null);
  useEffect(() => {
    if (["admin", "host"].includes(user.role)) {
      api.get(`/linen/latest`, { params: { house_id: id } }).then(({ data }) => setLatestLinen(data)).catch(() => {});
    }
  }, [id, user.role]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [savingStaff, setSavingStaff] = useState(false);
  const [taskCats, setTaskCats] = useState([]);
  const [taskCfg, setTaskCfg] = useState({});
  const [cfgDraft, setCfgDraft] = useState({});
  const [savingCfg, setSavingCfg] = useState(null);
  const [keySet, setKeySet] = useState(undefined);
  const [times, setTimes] = useState({ checkin: "16:00", checkout: "10:00" });
  const [savingTimes, setSavingTimes] = useState(false);
  const [owners, setOwners] = useState([]);
  const [savingOwner, setSavingOwner] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const coverInputRef = useRef(null);
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrForm, setAddrForm] = useState({ url: "", label: "" });
  const [savingAddr, setSavingAddr] = useState(false);
  const canManage = ["admin"].includes(user.role);
  const isOwnerRole = user.role === "client";
  const hasKeyAccess = user.role === "admin" || user.key_access;

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/houses/${id}/cover-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHouse((h) => ({ ...h, cover_photo_path: data.cover_photo_path, cover_updated_at: data.cover_updated_at }));
      toast.success(tx("Coverfoto bijgewerkt"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploadingCover(false); if (coverInputRef.current) coverInputRef.current.value = ""; }
  };

  const addrText = (h) => h.address_label || [h.address, h.city].filter(Boolean).join(", ");
  const addrHref = (h) => h.address_url || mapsUrl([h.address, h.city].filter(Boolean).join(", "));
  const openAddr = () => { setAddrForm({ label: addrText(house), url: addrHref(house) }); setAddrOpen(true); };
  const saveAddr = async () => {
    setSavingAddr(true);
    try {
      const { data } = await api.patch(`/houses/${id}/address-link`, { url: addrForm.url, label: addrForm.label });
      setHouse(data);
      toast.success(tx("Adreslink bijgewerkt"));
      setAddrOpen(false);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingAddr(false); }
  };

  const saveOwners = async (ids) => {
    setSavingOwner(true);
    try {
      const { data } = await api.patch(`/houses/${id}/owners`, { owner_ids: ids });
      setHouse(data);
      toast.success(tx("Eigenaren bijgewerkt"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingOwner(false); }
  };

  const saveTimes = async () => {
    setSavingTimes(true);
    try {
      const { data } = await api.patch(`/houses/${id}/checkin-times`, null, { params: { checkin: times.checkin, checkout: times.checkout } });
      setHouse(data);
      toast.success(tx("Incheck-/uitchecktijden opgeslagen"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingTimes(false); }
  };

  const loadTaskConfig = () => {
    api.get(`/houses/${id}/task-config`).then(({ data }) => {
      setTaskCfg(data);
      const d = {};
      Object.entries(data).forEach(([k, v]) => { d[k] = { subtasks: (v.subtasks || []).join("\n"), default_assignee_id: v.default_assignee_id || "" }; });
      setCfgDraft(d);
    }).catch(() => {});
  };

  useEffect(() => {
    api.get(`/houses/${id}`).then(({ data }) => { setHouse(data); setBooks(data.welcome_books || []); setSections(data.welcome_content?.sections || []); setTimes({ checkin: data.checkin_time || "16:00", checkout: data.checkout_time || "10:00" }); }).catch(() => { toast.error(tx("Woning niet gevonden")); navigate("/app/houses"); });
    if (canManage) {
      api.get("/calendar/list").then(({ data }) => setCalendars(data.calendars || [])).catch(() => {});
      api.get("/users").then(({ data }) => setStaffUsers(data.filter((u) => isStaff(u.role)))).catch(() => {});
      api.get("/task-categories").then(({ data }) => setTaskCats(data)).catch(() => {});
      loadTaskConfig();
    }
    if (user.role === "admin" || user.key_access) {
      api.get(`/keys/by-house/${id}`).then(({ data }) => setKeySet(data)).catch(() => setKeySet(null));
    }
    if (user.role === "admin") {
      api.get("/users").then(({ data }) => setOwners(data.filter((u) => u.role === "client"))).catch(() => {});
    }
  }, [id]);

  const saveTaskCfg = async (key) => {
    setSavingCfg(key);
    try {
      const d = cfgDraft[key] || { subtasks: "", default_assignee_id: "" };
      const subtasks = d.subtasks.split("\n").map((s) => s.trim()).filter(Boolean);
      await api.put(`/houses/${id}/task-config/${key}`, { subtasks, default_assignee_id: d.default_assignee_id || null });
      toast.success(tx("Sjabloon voor deze woning opgeslagen"));
      loadTaskConfig();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingCfg(null); }
  };

  const toggleStaff = async (uid, assigned) => {
    setSavingStaff(true);
    try {
      if (assigned) await api.delete(`/houses/${id}/staff/${uid}`);
      else await api.post(`/houses/${id}/staff/${uid}`);
      const { data } = await api.get(`/houses/${id}`);
      setHouse(data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingStaff(false); }
  };

  const saveContent = async () => {
    setSavingContent(true);
    try {
      const clean = sections.filter((s) => s.title.trim());
      const { data } = await api.put(`/houses/${id}/welcome-content`, { sections: clean });
      setHouse(data); setSections(data.welcome_content?.sections || []);
      toast.success(tx("Welkomstboek opgeslagen"));
      setEditContent(false);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingContent(false); }
  };
  const setSec = (i, k, v) => setSections(sections.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const addSec = () => setSections([...sections, { title: "", body: "" }]);
  const rmSec = (i) => setSections(sections.filter((_, idx) => idx !== i));

  const translateAll = async () => {
    setTranslating(true);
    const langs = ["en", "de", "fr", "es"];
    let ok = 0;
    for (const lang of langs) {
      try { await api.post(`/houses/${id}/translate-welcome-content?lang=${lang}`); ok++; toast.success(`${lang.toUpperCase()} vertaald`); }
      catch (err) { toast.error(`${lang.toUpperCase()}: ${apiError(err)}`); break; }
    }
    if (ok > 0) { const { data } = await api.get(`/houses/${id}`); setHouse(data); }
    setTranslating(false);
  };

  const saveBooks = async (nextBooks) => {
    setSavingBooks(true);
    try {
      const { data } = await api.put(`/houses/${id}/welcome-books`, { books: nextBooks });
      setHouse(data); setBooks(data.welcome_books || []);
      toast.success(tx("Welkomstboeken opgeslagen"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingBooks(false); }
  };
  const addBook = () => { if (!newBook.url) return; const next = [...books, { ...newBook, title: "" }]; setBooks(next); setNewBook({ language: "nl", url: "" }); saveBooks(next); };
  const removeBook = (idx) => { const next = books.filter((_, i) => i !== idx); setBooks(next); saveBooks(next); };

  const linkCalendar = async (calId) => {
    setSavingCal(true);
    try {
      const value = calId === NONE ? "" : calId;
      const { data } = await api.patch(`/houses/${id}/calendar`, null, { params: { calendar_id: value } });
      setHouse(data);
      toast.success(value ? tx("Agenda gekoppeld") : tx("Agenda ontkoppeld"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingCal(false); }
  };

  const del = async () => {
    if (!window.confirm(tx("Deze woning verwijderen?"))) return;
    try {
      await api.delete(`/houses/${id}`);
      toast.success(tx("Woning verwijderd"));
      navigate("/app/houses");
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!house) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

  return (
    <div className="animate-fade-up">
      <Button variant="ghost" onClick={() => navigate("/app/houses")} className="gap-2 mb-4 -ml-2" data-testid="back-btn"><ArrowLeft className="h-4 w-4" /> {tx("Terug")}</Button>

      <div className="rounded-2xl overflow-hidden border border-border shadow-sm relative">
        <div className="aspect-[21/9] bg-muted">
          <img src={houseCoverUrl(house)} alt={house.name} className="w-full h-full object-cover" data-testid="house-cover-image" />
        </div>
        {canManage && (
          <>
            <input ref={coverInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={uploadCover} data-testid="cover-file-input" />
            <Button size="sm" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover} className="absolute top-3 right-3 bg-black/55 hover:bg-black/70 text-white gap-1.5 backdrop-blur-sm border-0" data-testid="upload-cover-btn">
              {uploadingCover ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} {house.cover_photo_path ? tx("Wijzig foto") : tx("Foto toevoegen")}
            </Button>
          </>
        )}
      </div>

      <div className="flex items-start justify-between mt-6 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display font-bold text-3xl tracking-tight">{house.name}</h1>
            {user.role !== "guest" && house.computed_status && (
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                house.computed_status === "Bezet" ? "bg-red-100 text-red-700"
                : house.computed_status === "Schoonmaak" ? "bg-amber-100 text-amber-700"
                : "bg-emerald-100 text-emerald-700"
              }`} data-testid="house-status-badge">{house.computed_status}</span>
            )}
          </div>
          {(house.address || house.city || house.address_label || house.address_url) ? (
            <div className="flex items-center gap-2 mt-1.5">
              <a href={addrHref(house)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-muted-foreground hover:text-primary hover:underline w-fit" data-testid="house-address-link"><MapPin className="h-4 w-4 shrink-0" /> {addrText(house)}</a>
              {canManage && <button onClick={openAddr} className="text-muted-foreground hover:text-primary shrink-0" title={tx("Adreslink bewerken")} data-testid="edit-address-link-btn"><Pencil className="h-3.5 w-3.5" /></button>}
            </div>
          ) : canManage ? (
            <button onClick={openAddr} className="flex items-center gap-1.5 text-muted-foreground mt-1.5 hover:text-primary text-sm w-fit" data-testid="edit-address-link-btn"><MapPin className="h-4 w-4" /> {tx("Adreslink toevoegen")} <Pencil className="h-3.5 w-3.5" /></button>
          ) : null}
        </div>
        {canManage && <Button variant="outline" onClick={del} className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/5" data-testid="delete-house-btn"><Trash2 className="h-4 w-4" /> {tx("Verwijderen")}</Button>}
      </div>

      <Dialog open={addrOpen} onOpenChange={setAddrOpen}>
        <DialogContent className="max-w-md" data-testid="address-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{tx("Adreslink bewerken")}</DialogTitle>
            <DialogDescription>{tx("Pas de zichtbare tekst en de URL van de adreslink los van elkaar aan.")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{tx("Link-tekst")}</Label>
              <Input value={addrForm.label} onChange={(e) => setAddrForm({ ...addrForm, label: e.target.value })} className="mt-1" placeholder={tx("Bijv. Calle del Mar 12, Altea")} data-testid="address-label-input" />
            </div>
            <div>
              <Label className="text-xs">{tx("URL-link")}</Label>
              <Input value={addrForm.url} onChange={(e) => setAddrForm({ ...addrForm, url: e.target.value })} className="mt-1" placeholder="https://maps.google.com/..." data-testid="address-url-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddrOpen(false)} data-testid="address-cancel-btn">{tx("Annuleren")}</Button>
            <Button onClick={saveAddr} disabled={savingAddr} className="bg-primary hover:bg-primary/90 gap-1.5" data-testid="address-save-btn">
              {savingAddr ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {tx("Opslaan")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {["admin", "host", "client"].includes(user.role) && (
        <Button onClick={() => navigate(`/app/chat?house=${id}`)} className="gap-2 mt-4 bg-primary hover:bg-primary/90" data-testid="open-house-chat-btn">
          <MessageCircle className="h-4 w-4" /> {tx("Woningchat openen")}
        </Button>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 space-y-6">
          <NoticeBoard houseId={id} />
          <HouseDetailsCard house={house} canManage={canManage} onSaved={setHouse} />
          {(books.length > 0 || house.welcome_book_url || house.welcome_content?.sections?.length > 0 || canManage) && (
            <div className="bg-card rounded-xl border border-border p-6 shadow-sm" data-testid="welcome-books-card">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-secondary" />
                  <h2 className="font-display font-semibold text-lg">{tx("Welkomstboek")}</h2>
                </div>
                {canManage && (
                  <div className="flex items-center gap-1">
                    <WelcomeVisibilityMenu house={house} onSaved={(wh) => setHouse((h) => ({ ...h, welcome_hidden: wh }))} />
                    <Button variant="ghost" size="sm" onClick={() => setManageOpen(true)} className="gap-1.5 text-muted-foreground" data-testid="manage-books-btn">
                      <Settings className="h-4 w-4" /> {tx("Beheer")}
                    </Button>
                  </div>
                )}
              </div>
              {(house.welcome_content?.sections?.length > 0) && (
                <div className="mb-3">
                  <Button onClick={() => setContentView(true)} className="bg-secondary hover:bg-secondary/90 gap-2" data-testid="open-inapp-book">
                    <BookOpen className="h-4 w-4" /> {tx("Open welkomstboek (in-app)")}
                  </Button>
                  {canManage && (
                    <Button variant="outline" size="sm" onClick={() => setEditContent(true)} className="ml-2 gap-1.5" data-testid="edit-content-btn">
                      <Globe className="h-3.5 w-3.5" /> {tx("Inhoud bewerken")}
                    </Button>
                  )}
                  {canManage && (
                    <Button variant="outline" size="sm" onClick={translateAll} disabled={translating} className="ml-2 gap-1.5" data-testid="translate-btn">
                      {translating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5" />} {tx("Vertaal (EN/DE/FR/ES)")}
                    </Button>
                  )}
                </div>
              )}
              {books.length > 0 ? (
                <>
                  <p className="text-sm text-muted-foreground mb-3">{tx("Open het digitale welkomstboek")}{books.length > 1 ? tx(" in jouw taal") : ""}.</p>
                  <div className="flex flex-wrap gap-2">
                    {books.map((b, i) => (
                      <Button key={i} onClick={() => setViewBook(b)} className="bg-secondary hover:bg-secondary/90 gap-2" data-testid={`open-book-${b.language}`}>
                        <span>{FLAG[b.language] || "📖"}</span> {LANGS[b.language] || b.language.toUpperCase()}
                      </Button>
                    ))}
                  </div>
                </>
              ) : house.welcome_book_url ? (
                <a href={house.welcome_book_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-secondary font-medium hover:underline" data-testid="welcomebook-link">
                  <ExternalLink className="h-4 w-4" /> {tx("Open welkomstboek")}
                </a>
              ) : (
                !(house.welcome_content?.sections?.length > 0) && (
                  <p className="text-sm text-muted-foreground">{tx("Nog geen welkomstboek.")} {canManage && tx("Voeg er een toe via 'Beheer' of 'Inhoud bewerken'.")}</p>
                )
              )}
            </div>
          )}
        </div>

        <div className="space-y-6">
          {!["client", "guest"].includes(user.role) && <RecentCommentsCard houseId={id} />}
          {canManage && (
            <div className="bg-card rounded-xl border border-border p-6 shadow-sm" data-testid="house-owner-card">
              <div className="flex items-center gap-2 mb-1">
                <UsersIcon className="h-4 w-4 text-secondary" />
                <h2 className="font-display font-semibold text-lg">{tx("Eigenaren")}</h2>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{tx("Koppel één of meerdere geregistreerde eigenaren; zij zien dan alleen deze woning(en).")}</p>
              {(() => {
                const ownerIds = house.owner_ids?.length ? house.owner_ids : (house.owner_id ? [house.owner_id] : []);
                return (
                  <div className="space-y-1.5 max-h-56 overflow-y-auto rounded-md border border-border p-2" data-testid="house-owners-list">
                    {owners.map((o) => (
                      <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`owner-opt-${o.id}`}>
                        <input
                          type="checkbox"
                          disabled={savingOwner}
                          checked={ownerIds.includes(o.id)}
                          onChange={() => saveOwners(ownerIds.includes(o.id) ? ownerIds.filter((x) => x !== o.id) : [...ownerIds, o.id])}
                          className="h-4 w-4 rounded border-input"
                        />
                        <span>{o.name} <span className="text-muted-foreground">· {o.email}</span></span>
                      </label>
                    ))}
                  </div>
                );
              })()}
              {owners.length === 0 && <p className="text-xs text-muted-foreground mt-2">{tx("Nog geen gebruikers met de rol Eigenaar. Maak er een aan via Gebruikers.")}</p>}
            </div>
          )}
          {["admin", "host"].includes(user.role) && latestLinen && (
          <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
            {["admin", "host"].includes(user.role) && latestLinen && (
              <div data-testid="house-latest-linen">
                <div className="flex items-center gap-2 text-sm font-medium"><Shirt className="h-4 w-4 text-secondary" /> {tx("Laatste linnen")}</div>
                <p className="text-[11px] text-muted-foreground mt-0.5 pl-6">{new Date(latestLinen.created_at).toLocaleDateString(uiLocale(), { day: "2-digit", month: "short", year: "numeric" })}</p>
                <div className="flex flex-wrap gap-1.5 mt-1.5 pl-6">
                  {LINEN_ITEMS.filter(({ key }) => latestLinen.items?.[key] > 0).map(({ key, label }) => (
                    <span key={key} className="text-xs bg-muted px-2 py-0.5 rounded-full">{label}: {latestLinen.items[key]}</span>
                  ))}
                  {latestLinen.anders && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">{tx("Anders:")} {latestLinen.anders}</span>}
                  {LINEN_ITEMS.every(({ key }) => !(latestLinen.items?.[key] > 0)) && !latestLinen.anders && <span className="text-xs text-muted-foreground">{tx("Geen items")}</span>}
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      <div className="columns-1 md:columns-2 2xl:columns-3 gap-6 mt-6 [&>*]:break-inside-avoid [&>*]:mb-6" data-testid="house-cards-grid">
          {(canManage || isOwnerRole) && (
            <ServiceRatesCard
              houseId={id}
              rates={house.service_rates}
              canEdit={canManage}
              onSaved={(r) => setHouse((h) => ({ ...h, service_rates: r }))}
            />
          )}

          {hasKeyAccess && (
            <div className="bg-card rounded-xl border border-border p-6 shadow-sm" data-testid="house-keys-card">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-secondary" />
                  <h2 className="font-display font-semibold text-lg">{tx("Sleutelbeheer")}</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate("/app/keys")} className="gap-1.5 text-muted-foreground" data-testid="goto-keys-btn">
                  <Settings className="h-4 w-4" /> {tx("Beheer")}
                </Button>
              </div>
              {keySet === undefined ? (
                <div className="flex py-2"><Loader2 className="h-4 w-4 animate-spin text-primary" /></div>
              ) : !keySet ? (
                <p className="text-sm text-muted-foreground">{tx("Nog geen sleutelgegevens voor deze woning. Voeg ze toe via Sleutelbeheer.")}</p>
              ) : (
                <div className="space-y-2 text-sm" data-testid="house-key-info">
                  <div className="flex justify-between items-center gap-3"><span className="text-muted-foreground">{tx("Beheerder")}</span><span className="font-medium text-right" data-testid="house-key-beheerder">{keySet.beheerder || "—"}</span></div>
                  <div className="flex justify-between items-center gap-3"><span className="text-muted-foreground">{tx("Set C")}</span><KeyBadge value={keySet.set_c} /></div>
                  <div className="flex justify-between items-center gap-3"><span className="text-muted-foreground">{tx("Set G")}</span><KeyBadge value={keySet.set_g} /></div>
                  <div className="flex justify-between items-center gap-3"><span className="text-muted-foreground">{tx("2e Set G")}</span><KeyBadge value={keySet.set_g2} /></div>
                  <div className="flex justify-between items-center gap-3"><span className="text-muted-foreground">{tx("Set E")}</span><KeyBadge value={keySet.set_e} /></div>
                  {keySet.bijzonderheden && <div className="border-t border-border pt-2 text-muted-foreground italic">{keySet.bijzonderheden}</div>}
                </div>
              )}
            </div>
          )}

          {canManage && (
            <div className="bg-card rounded-xl border border-border p-6 shadow-sm" data-testid="house-times-card">
              <div className="flex items-center gap-2 mb-1">
                <CalendarDays className="h-4 w-4 text-secondary" />
                <h2 className="font-display font-semibold text-lg">{tx("Incheck- & uitchecktijd")}</h2>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{tx("Deze tijden worden in het WhatsApp-bericht aan komende gasten getoond.")}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="checkin-time">{tx("Vanaf (incheck)")}</Label>
                  <Input id="checkin-time" type="time" value={times.checkin} onChange={(e) => setTimes({ ...times, checkin: e.target.value })} className="mt-1" data-testid="checkin-time-input" />
                </div>
                <div>
                  <Label htmlFor="checkout-time">{tx("Tot (uitcheck)")}</Label>
                  <Input id="checkout-time" type="time" value={times.checkout} onChange={(e) => setTimes({ ...times, checkout: e.target.value })} className="mt-1" data-testid="checkout-time-input" />
                </div>
              </div>
              <Button onClick={saveTimes} disabled={savingTimes} className="mt-3 bg-primary hover:bg-primary/90 gap-2" data-testid="save-times-btn">
                {savingTimes && <Loader2 className="h-4 w-4 animate-spin" />} {tx("Opslaan")}
              </Button>
            </div>
          )}

          {canManage && (
            <div className="bg-card rounded-xl border border-border p-6 shadow-sm" data-testid="house-calendar-card">
              <div className="flex items-center gap-2 mb-1">
                <CalendarDays className="h-4 w-4 text-secondary" />
                <h2 className="font-display font-semibold text-lg">{tx("Google Agenda")}</h2>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{tx("Afspraken voor deze woning worden op deze agenda gezet.")}</p>
              {house.google_calendar_id ? (
                <div className="flex items-center gap-1.5 text-sm font-medium text-secondary mb-3">
                  <Check className="h-4 w-4" /> {calendars.find((c) => c.id === house.google_calendar_id)?.summary || tx("Gekoppeld")}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground mb-3">{tx("Nog geen agenda gekoppeld.")}</div>
              )}
              <Select value={house.google_calendar_id || NONE} onValueChange={linkCalendar} disabled={savingCal || calendars.length === 0}>
                <SelectTrigger data-testid="house-calendar-select"><SelectValue placeholder={calendars.length ? tx("Kies een agenda") : tx("Koppel eerst Google Agenda")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{tx("Geen agenda")}</SelectItem>
                  {calendars.map((c) => <SelectItem key={c.id} value={c.id}>{c.summary}{c.primary ? tx(" (hoofd)") : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {canManage && (
            <div className="bg-card rounded-xl border border-border p-6 shadow-sm" data-testid="house-staff-card">
              <div className="flex items-center gap-2 mb-1">
                <UsersIcon className="h-4 w-4 text-secondary" />
                <h2 className="font-display font-semibold text-lg">{tx("Medewerkers")}</h2>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{tx("Gekoppelde medewerkers zien deze woning met agenda, taken en boekingen.")}</p>
              {staffUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground">{tx("Nog geen medewerkers. Maak ze aan via Gebruikers.")}</p>
              ) : (
                <div className="space-y-1.5">
                  {staffUsers.map((s) => {
                    const assigned = (house.staff_ids || []).includes(s.id);
                    return (
                      <button
                        key={s.id}
                        onClick={() => toggleStaff(s.id, assigned)}
                        disabled={savingStaff}
                        data-testid={`staff-toggle-${s.id}`}
                        className={`w-full flex items-center gap-3 rounded-lg border p-2.5 text-left transition-colors ${assigned ? "border-primary/40 bg-primary/5" : "border-border hover:bg-accent"}`}
                      >
                        <span className={`grid place-items-center h-5 w-5 rounded border shrink-0 ${assigned ? "bg-primary border-primary text-white" : "border-input"}`}>
                          {assigned && <Check className="h-3.5 w-3.5" />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="text-sm font-medium block truncate">{s.name}</span>
                          <span className="text-xs text-muted-foreground">{ROLE_LABELS[s.role]}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {canManage && (
            <div className="bg-card rounded-xl border border-border p-6 shadow-sm" data-testid="house-taskconfig-card">
              <div className="flex items-center gap-2 mb-1">
                <ListChecks className="h-4 w-4 text-secondary" />
                <h2 className="font-display font-semibold text-lg">{tx("Taken-sjabloon per categorie")}</h2>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{tx("Pas de checklist en vaste medewerker per categorie aan voor déze woning. Leeg = globale standaard.")}</p>
              <div className="space-y-4">
                {taskCats.map((c) => {
                  const d = cfgDraft[c.key] || { subtasks: "", default_assignee_id: "" };
                  const src = taskCfg[c.key]?.source;
                  return (
                    <div key={c.key} className="rounded-lg border border-border p-3" data-testid={`taskcfg-${c.key}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">{c.label}</span>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{src === "house" ? "aangepast" : "globaal"}</span>
                      </div>
                      <Textarea
                        value={d.subtasks}
                        onChange={(e) => setCfgDraft({ ...cfgDraft, [c.key]: { ...d, subtasks: e.target.value } })}
                        rows={4}
                        placeholder={tx("Eén subtaak per regel")}
                        className="text-sm font-mono"
                        data-testid={`taskcfg-subtasks-${c.key}`}
                      />
                      <div className="flex items-center gap-2 mt-2">
                        <Select value={d.default_assignee_id || "none"} onValueChange={(v) => setCfgDraft({ ...cfgDraft, [c.key]: { ...d, default_assignee_id: v === "none" ? "" : v } })}>
                          <SelectTrigger className="h-9 text-sm flex-1" data-testid={`taskcfg-assignee-${c.key}`}><SelectValue placeholder={tx("Vaste medewerker")} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{tx("Geen vaste medewerker")}</SelectItem>
                            {staffUsers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {ROLE_LABELS[s.role]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button size="sm" onClick={() => saveTaskCfg(c.key)} disabled={savingCfg === c.key} className="bg-primary hover:bg-primary/90 shrink-0" data-testid={`taskcfg-save-${c.key}`}>
                          {savingCfg === c.key ? <Loader2 className="h-4 w-4 animate-spin" /> : tx("Opslaan")}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
      </div>

      <Dialog open={!!viewBook} onOpenChange={(o) => !o && setViewBook(null)}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 overflow-hidden" data-testid="book-viewer">
          <DialogHeader className="px-5 pt-4 pb-2">
            <DialogTitle className="font-display flex items-center gap-2">
              {FLAG[viewBook?.language]} {tx("Welkomstboek")} {house.name} · {LANGS[viewBook?.language] || ""}
              {viewBook?.url && (
                <a href={viewBook.url} target="_blank" rel="noreferrer" className="ml-auto text-sm text-secondary inline-flex items-center gap-1 hover:underline" data-testid="book-newtab">
                  <ExternalLink className="h-3.5 w-3.5" /> {tx("Nieuw tabblad")}
                </a>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">{tx("Digitaal welkomstboek voor")} {house.name}</DialogDescription>
          </DialogHeader>
          {viewBook && <iframe title="welkomstboek" src={viewBook.url} className="w-full h-[75vh] border-0" allowFullScreen />}
        </DialogContent>
      </Dialog>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent data-testid="manage-books-dialog">
          <DialogHeader><DialogTitle className="font-display">{tx("Welkomstboeken beheren")}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {books.length === 0 && <p className="text-sm text-muted-foreground">{tx("Nog geen welkomstboeken.")}</p>}
            {books.map((b, i) => (
              <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg border border-border">
                <span>{FLAG[b.language] || "📖"}</span>
                <span className="text-sm font-medium w-24 shrink-0">{LANGS[b.language] || b.language}</span>
                <span className="text-xs text-muted-foreground flex-1 truncate">{b.url}</span>
                <Button size="icon" variant="ghost" onClick={() => removeBook(i)} className="text-destructive h-8 w-8" data-testid={`remove-book-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
          <div className="border-t border-border pt-3 space-y-2">
            <Label>{tx("Nieuw welkomstboek toevoegen")}</Label>
            <div className="flex gap-2">
              <Select value={newBook.language} onValueChange={(v) => setNewBook({ ...newBook, language: v })}>
                <SelectTrigger className="w-36" data-testid="new-book-lang"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(LANGS).map(([k, v]) => <SelectItem key={k} value={k}>{FLAG[k]} {v}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="https://..." value={newBook.url} onChange={(e) => setNewBook({ ...newBook, url: e.target.value })} data-testid="new-book-url" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={addBook} disabled={savingBooks || !newBook.url} className="bg-primary hover:bg-primary/90 gap-2" data-testid="add-book-btn">
              {savingBooks ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {tx("Toevoegen")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={contentView} onOpenChange={setContentView}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="inapp-book-viewer">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><BookOpen className="h-5 w-5 text-secondary" /> {tx("Welkomstboek ·")} {house.name}</DialogTitle>
            <DialogDescription className="sr-only">{tx("In-app welkomstboek voor")} {house.name}</DialogDescription>
          </DialogHeader>
          {(() => {
            const i18n = house.welcome_content_i18n || {};
            const langs = ["nl", ...Object.keys(i18n)];
            const active = langs.includes(bookLang) ? bookLang : "nl";
            const secs = active === "nl" ? (house.welcome_content?.sections || []) : (i18n[active]?.sections || []);
            return (
              <>
                {langs.length > 1 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {langs.map((l) => (
                      <button key={l} onClick={() => setBookLang(l)} data-testid={`book-lang-${l}`}
                        className={`text-sm px-3 py-1 rounded-full font-medium transition-colors ${active === l ? "bg-secondary text-white" : "bg-muted text-muted-foreground hover:bg-accent"}`}>
                        {FLAG[l] || ""} {LANGS[l] || l.toUpperCase()}
                      </button>
                    ))}
                  </div>
                )}
                <div className="space-y-5">
                  {secs.map((s, i) => (
                    <div key={i} className="border-b border-border pb-4 last:border-0" data-testid={`book-section-${i}`}>
                      <h3 className="font-display font-semibold text-lg text-secondary mb-1.5">{s.title}</h3>
                      <p className="text-sm text-foreground/90 whitespace-pre-line leading-relaxed">{s.body}</p>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={editContent} onOpenChange={setEditContent}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="edit-content-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{tx("Welkomstboek-inhoud bewerken")}</DialogTitle>
            <DialogDescription className="sr-only">{tx("Secties bewerken")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {sections.map((s, i) => (
              <div key={i} className="rounded-lg border border-border p-3 space-y-2" data-testid={`edit-section-${i}`}>
                <div className="flex items-center gap-2">
                  <Input value={s.title} onChange={(e) => setSec(i, "title", e.target.value)} placeholder={tx("Sectietitel")} className="font-medium" data-testid={`section-title-${i}`} />
                  <Button size="icon" variant="ghost" onClick={() => rmSec(i)} className="text-destructive shrink-0"><Trash2 className="h-4 w-4" /></Button>
                </div>
                <textarea value={s.body} onChange={(e) => setSec(i, "body", e.target.value)} rows={5} placeholder={tx("Inhoud...")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid={`section-body-${i}`} />
              </div>
            ))}
            <Button variant="outline" onClick={addSec} className="gap-2 w-full" data-testid="add-section-btn"><Plus className="h-4 w-4" /> {tx("Sectie toevoegen")}</Button>
          </div>
          <DialogFooter>
            <Button onClick={saveContent} disabled={savingContent} className="bg-primary hover:bg-primary/90 gap-2" data-testid="save-content-btn">
              {savingContent ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {tx("Opslaan")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
